import React, { useMemo, useState } from 'react'
import injectSheet from 'react-jss'
import styles from './action-comment.styles'
import { PobbleAvatar } from 'pobble-components/dist/pobble-avatar'
import Button from 'pobble-components/dist/button'
import classNames from 'classnames'
import PaperclipIcon from 'react-icons/lib/fa/paperclip'
import CheckIcon from 'react-icons/lib/fa/check'
import { distanceInWords } from 'date-fns'
import ContextMenu from './context-menu/context-menu.container'
import { Link, withRouter } from 'react-router-dom'
import TextArea from 'pobble-components/dist/form-elements/form-text-area'
import { Formik } from 'formik'
import * as Yup from 'yup'
import ActionCommentReply from './action-comment-reply/action-comment-reply.container'
import { Modal, ModalContent, ModalFooter } from 'pobble-components/dist/modal'
import duck from '../../../../../common/images/duck.gif'
import Tooltip from 'pobble-components/dist/tooltip'

const ActionComment = ({
  classes,
  id,
  avatar,
  name,
  content,
  timestamp,
  contributionId,
  onMarkedAsConclusion,
  onUnmarkedAsConclusion,
  isConclusion,
  concludedBy,
  isAFConcluded,
  contributorsCount,
  attachment,
  attachmentName,
  canDelete,
  edited,
  workId,
  assessmentFileWorks,
  assessmentFileId,
  workRef,
  isAFClosed,
  onReply,
  replies,
  localAuthority,
  isLocalAuthorityManager
}) => {
  const postedAt = useMemo(
    () => (!timestamp ? '' : distanceInWords(new Date(), new Date(timestamp))),
    [timestamp]
  )

  let [replyVisible, showReply] = useState(0)

  const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      className={classNames(classes.wrapper, {
        [classes.conclusion]: isConclusion
      })}>
      {isConclusion && (
        <div className={classes.markedAsConclusion}>
          Marked as a conclusion <span>by {concludedBy}</span>
        </div>
      )}
      <div className={classes.container}>
        <PobbleAvatar avatar={avatar} name={name} className={classes.avatar} />
        <div className={classes.content}>
          <div className={classes.action}>
            <strong className={classes.name}>{name}</strong>{' '}
            {localAuthority && isLocalAuthorityManager && (
              <Tooltip content={localAuthority + ', LA Manager'}>
                <CheckIcon className={classes.check} />
              </Tooltip>
            )}{' '}
            said {postedAt} ago {edited && <span>(edited)</span>}
            {!isConclusion && canDelete && (
              <div className={classes.dots}>
                <ContextMenu id={id} contributionId={contributionId} />
              </div>
            )}
          </div>
          <p>{content}</p>
          {workId && workRef && (
            <div className={classes.ref}>
              Ref:{' '}
              <Link
                className={classes.link}
                to={
                  '/assessment-files/' + assessmentFileId + '/works/' + workId
                }>
                #{assessmentFileWorks.indexOf(workId) + 1}: {workRef.title}
              </Link>
            </div>
          )}
          {attachment && (
            <a
              className={classes.attachment}
              href={attachment}
              target="_blank"
              rel="noopener noreferrer">
              <PaperclipIcon /> {attachmentName}
            </a>
          )}

          <div className={classes.replies}>
            {replies &&
              replies.map(reply => (
                <ActionCommentReply
                  key={reply.id}
                  id={reply.id}
                  reply={reply}
                  parentId={id}
                />
              ))}

            {!!replyVisible && (
              <Formik
                initialValues={{
                  reply: ''
                }}
                validationSchema={Yup.object().shape({
                  reply: Yup.string().required('The reply should not be empty')
                })}
                onSubmit={({ reply }) => {
                  onReply(assessmentFileId, contributionId, reply, id).then(
                    res => showReply((replyVisible = false))
                  )
                }}>
                {props => {
                  const {
                    handleSubmit,
                    handleChange,
                    values,
                    errors,
                    isValid
                  } = props
                  return (
                    <form onSubmit={handleSubmit} className={classes.replyForm}>
                      <TextArea
                        validationError={errors.reply}
                        name="reply"
                        value={values.reply}
                        onChange={handleChange}
                        autoFocus
                      />
                      <div className={classes.replyActions}>
                        <Button
                          simple
                          action={() => showReply((replyVisible = false))}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={!isValid}>
                          Reply
                        </Button>
                      </div>
                    </form>
                  )
                }}
              </Formik>
            )}
          </div>

          <footer className={classes.footer}>
            <Button
              disabled={isConclusion || isAFConcluded || isAFClosed}
              action={() => showReply((replyVisible = !replyVisible))}
              className={classes.button}>
              Reply
            </Button>

            {!isConclusion && (
              <Button
                action={() => setIsOpen(!isOpen)}
                className={classes.button}
                disabled={isConclusion || isAFConcluded || isAFClosed}>
                Mark{isConclusion ? 'ed' : ''} as conclusion
              </Button>
            )}
            {isConclusion && (
              <Button
                action={() => onUnmarkedAsConclusion(contributionId, id)}
                className={classes.button}
                disabled={isAFClosed}>
                Unmark as conclusion
              </Button>
            )}
          </footer>
        </div>
      </div>
      <Modal closeButton requestClose={setIsOpen} isOpen={isOpen} big>
        <ModalContent legacy className={classes.conclusionContent}>
          <img src={duck} alt="Duck" />
          <h3>Great!</h3>
          <p>
            You are about to select a conclusion for this discussion.
            <br />
            Contributors to this moderation file ({contributorsCount}{' '}
            {contributorsCount > 1 ? 'people' : 'person'}) will be notified.{' '}
          </p>
        </ModalContent>
        <ModalFooter className={classes.footer}>
          <div className={classes.conclusionFooter}>
            <Button simple action={setIsOpen}>
              Cancel
            </Button>
            <Button
              tall
              action={() =>
                onMarkedAsConclusion(contributionId, id) && setIsOpen()
              }>
              Mark as conclusion
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default withRouter(injectSheet(styles)(ActionComment))
